import * as React from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

export type ResponsiveTabItem = {
  label: React.ReactNode
  value: string
}

/**
 * Breakpoint at which the layout swaps from a Select (below) to a TabsList
 * (at and above). Class names are listed literally so Tailwind can scan them.
 */
type ResponsiveTabsBreakpoint = 'lg' | '@3xl/page' | '@3xl/table' | '@7xl/page' | '@7xl/table'

const breakpointClasses: Record<ResponsiveTabsBreakpoint, { list: string; select: string }> = {
  '@3xl/page': { list: 'hidden @3xl/page:inline-flex', select: '@3xl/page:hidden' },
  '@3xl/table': { list: 'hidden @3xl/table:inline-flex', select: '@3xl/table:hidden' },
  '@7xl/page': { list: 'hidden @7xl/page:inline-flex', select: '@7xl/page:hidden' },
  '@7xl/table': { list: 'hidden @7xl/table:inline-flex', select: '@7xl/table:hidden' },
  lg: { list: 'hidden lg:inline-flex', select: 'lg:hidden' },
}

type ResponsiveTabsProps = {
  /** Where to swap Select <-> Tabs. Defaults to `lg`. */
  breakpoint?: ResponsiveTabsBreakpoint
  /** TabsContent elements, one per item value. */
  children?: React.ReactNode
  className?: string
  /** Wraps the optional header together with the Select/Tabs (e.g. `space-y-3`). */
  controlsClassName?: string
  defaultValue?: string
  /** Rendered above the Select/Tabs, inside the controls wrapper. */
  header?: React.ReactNode
  /** Triggers, rendered as both Select options (small) and TabsTriggers (large). */
  items: ResponsiveTabItem[]
  listClassName?: string
  onValueChange?(value: string): void
  selectTriggerClassName?: string
  value?: string
  variant?: 'segment' | 'line'
}

export const ResponsiveTabs = ({
  breakpoint = 'lg',
  children,
  className,
  controlsClassName,
  defaultValue,
  header,
  items,
  listClassName,
  onValueChange,
  selectTriggerClassName,
  value,
  variant = 'segment',
}: ResponsiveTabsProps) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? items[0]?.value)
  const currentValue = value ?? internalValue

  const handleValueChange = (next: string) => {
    setInternalValue(next)
    onValueChange?.(next)
  }

  const { list, select } = breakpointClasses[breakpoint]

  return (
    <Tabs className={className} onValueChange={handleValueChange} value={currentValue}>
      <div className={controlsClassName}>
        {header}

        <div className={select}>
          <Select onValueChange={handleValueChange} value={currentValue}>
            <SelectTrigger className={cn('w-fit min-w-44', selectTriggerClassName)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {items.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsList className={cn(list, listClassName)} variant={variant}>
          {items.map(item => (
            <TabsTrigger key={item.value} value={item.value} variant={variant}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {children}
    </Tabs>
  )
}
